import { DeleteOutlined, PlusOutlined, ProfileOutlined } from '@ant-design/icons';

import { Button, Checkbox, Empty, Field, Input, Select, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { KIEU_TIEU_CHI, MUC_NGHIEM_TRONG, THU_TU_KIEU } from '../constants';
import type { CanCu, KieuTieuChi, MucNghiemTrong, TieuChi } from '../types';

export interface ChiTietTieuChiProps {
  /** Không có tiêu chí nào đang chọn thì hiện lời mời bấm vào cột giữa. */
  tieuChi?: TieuChi;
  duocSua: boolean;
  onDoi: (f: (tc: TieuChi) => void) => void;
}

/**
 * Cột phải của Thư viện tiêu chí: sửa chi tiết một tiêu chí, theo giao diện v3.
 *
 * Mọi trường ở đây là thứ **mô hình đọc khi chấm** (đặc tả mục 7.3): `huong_dan_cham` đi thẳng vào lời nhắc,
 * `vi_du_dat`/`vi_du_khong_dat` neo phán quyết, `can_cu` là căn cứ pháp luật mà hàng rào nguồn (mục 8.4) dò
 * lại. Viết hời hợt ở đây thì lượt chấm nào cũng lệch, nên nhãn nói rõ từng ô dùng vào việc gì.
 *
 * ⚠️ Không sửa `ma`: mã là khoá ổn định mà `ket_qua_tieu_chi.ma` trỏ vào (đặc tả mục 5.5). Đổi nó là mất nối
 * với mọi kết quả đã chấm, nên nó chỉ sinh một lần lúc thêm tiêu chí.
 */
export function ChiTietTieuChi({ tieuChi, duocSua, onDoi }: ChiTietTieuChiProps) {
  const { t } = useT();

  if (!tieuChi) {
    return (
      <div className="flex h-full items-center overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
        <Empty
          icon={ProfileOutlined}
          title={t('thamDinh.chiTiet.chuaChon')}
          description={t('thamDinh.chiTiet.chuaChonMo')}
        />
      </div>
    );
  }

  const canCu = tieuChi.can_cu ?? [];

  function doiCanCu(i: number, f: (c: CanCu) => void) {
    onDoi((tc) => {
      tc.can_cu ??= [];
      f(tc.can_cu[i]!);
    });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      <div className="border-b border-default px-4 py-3">
        <h2 className="truncate font-medium text-content-primary">{tieuChi.ten}</h2>
        <p className="truncate font-mono text-2xs text-content-muted">{tieuChi.ma}</p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <Field label={t('thamDinh.chiTiet.ten')}>
          {(p) => (
            <Input {...p} disabled={!duocSua} value={tieuChi.ten} onChange={(e) => onDoi((tc) => { tc.ten = e.target.value; })} />
          )}
        </Field>

        <Field label={t('thamDinh.chiTiet.kieu')} hint={t('thamDinh.chiTiet.kieuMo')}>
          {(p) => (
            <Select<KieuTieuChi>
              {...p}
              className="w-full"
              disabled={!duocSua}
              value={tieuChi.kieu}
              options={THU_TU_KIEU.map((k) => ({ value: k, label: t(KIEU_TIEU_CHI[k]) }))}
              onChange={(v) => onDoi((tc) => { tc.kieu = v; })}
            />
          )}
        </Field>

        {/* Thang điểm tối đa chỉ có nghĩa với kiểu `thang_diem` (đặc tả mục 5.2). */}
        {tieuChi.kieu === 'thang_diem' && (
          <Field label={t('thamDinh.chiTiet.thangToiDa')}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                max={100}
                disabled={!duocSua}
                value={tieuChi.thang_toi_da ?? 1}
                onChange={(e) => onDoi((tc) => { tc.thang_toi_da = Number(e.target.value); })}
              />
            )}
          </Field>
        )}

        <Field label={t('thamDinh.chiTiet.mucNghiemTrong')}>
          {(p) => (
            <Select<MucNghiemTrong>
              {...p}
              className="w-full"
              disabled={!duocSua}
              value={tieuChi.muc_nghiem_trong}
              options={(['thap', 'trung_binh', 'cao', 'nghiem_trong'] as const).map((m) => ({
                value: m,
                label: t(MUC_NGHIEM_TRONG[m].khoa),
              }))}
              onChange={(v) => onDoi((tc) => { tc.muc_nghiem_trong = v; })}
            />
          )}
        </Field>

        <Checkbox
          disabled={!duocSua}
          checked={Boolean(tieuChi.loai_tru)}
          onChange={(e) => onDoi((tc) => { tc.loai_tru = e.target.checked; })}
        >
          <span className="text-sm">{t('thamDinh.chiTiet.loaiTru')}</span>
          <span className="block text-2xs text-content-muted">{t('thamDinh.chiTiet.loaiTruMo')}</span>
        </Checkbox>

        <Field label={t('thamDinh.chiTiet.huongDan')} hint={t('thamDinh.chiTiet.huongDanMo')}>
          {(p) => (
            <Textarea
              {...p}
              rows={4}
              disabled={!duocSua}
              value={tieuChi.huong_dan_cham ?? ''}
              onChange={(e) => onDoi((tc) => { tc.huong_dan_cham = e.target.value; })}
            />
          )}
        </Field>

        <Field label={t('thamDinh.chiTiet.viDuDat')} hint={t('thamDinh.chiTiet.viDuMo')}>
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              disabled={!duocSua}
              value={tieuChi.vi_du_dat ?? ''}
              onChange={(e) => onDoi((tc) => { tc.vi_du_dat = e.target.value; })}
            />
          )}
        </Field>

        <Field label={t('thamDinh.chiTiet.viDuKhongDat')}>
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              disabled={!duocSua}
              value={tieuChi.vi_du_khong_dat ?? ''}
              onChange={(e) => onDoi((tc) => { tc.vi_du_khong_dat = e.target.value; })}
            />
          )}
        </Field>

        {/* Căn cứ pháp luật: hàng rào nguồn ở đặc tả mục 8.4 dò lại chính những miền này. */}
        <section aria-label={t('thamDinh.chiTiet.canCu')}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-content-primary">{t('thamDinh.chiTiet.canCu')}</p>
              <p className="text-2xs text-content-muted">{t('thamDinh.chiTiet.canCuMo')}</p>
            </div>
            {duocSua && (
              <Button
                variant="ghost"
                size="sm"
                icon={<PlusOutlined />}
                onClick={() => onDoi((tc) => { (tc.can_cu ??= []).push({ ten: '' }); })}
              >
                {t('thamDinh.chiTiet.themCanCu')}
              </Button>
            )}
          </div>

          <ul className="space-y-2">
            {canCu.map((c, i) => (
              <li key={i} className="rounded border border-default p-2">
                <div className="flex gap-2">
                  <Input
                    className="flex-1"
                    disabled={!duocSua}
                    placeholder={t('thamDinh.chiTiet.canCuTen')}
                    aria-label={t('thamDinh.chiTiet.canCuTen')}
                    value={c.ten}
                    onChange={(e) => doiCanCu(i, (x) => { x.ten = e.target.value; })}
                  />
                  {duocSua && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('thamDinh.chiTiet.xoaCanCu')}
                      onClick={() => onDoi((tc) => { tc.can_cu?.splice(i, 1); })}
                    >
                      <DeleteOutlined />
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    className="w-32"
                    disabled={!duocSua}
                    placeholder={t('thamDinh.chiTiet.canCuDieu')}
                    aria-label={t('thamDinh.chiTiet.canCuDieu')}
                    value={c.dieu ?? ''}
                    onChange={(e) => doiCanCu(i, (x) => { x.dieu = e.target.value; })}
                  />
                  <Input
                    className="flex-1"
                    disabled={!duocSua}
                    placeholder={t('thamDinh.chiTiet.canCuUrl')}
                    aria-label={t('thamDinh.chiTiet.canCuUrl')}
                    value={c.url ?? ''}
                    onChange={(e) => doiCanCu(i, (x) => { x.url = e.target.value; })}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
